import { Data } from '.generated/templates/Feature.Data.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { useEffect, useState } from 'react';
import { classnames } from 'tailwindcss-classnames';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import { TextWithOptionalLineBreaks } from 'components/helpers/Heading';
import Collapse from 'components/helpers/SvgIcon/icons/collape';
import Expand from 'components/helpers/SvgIcon/icons/expand';
import ArrowCollapse from 'components/helpers/SvgIcon/icons/arrow-collape';
import ArroweExpand from 'components/helpers/SvgIcon/icons/arrow-expand';
import { usePageSectionTheme } from 'components/globalContext/PageSectionThemeContext';
import { useIsNRC } from 'lib/util/site-configuration';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { gtmDatalayerPush, ContentDataLayerObject } from 'lib/util/gtm-datalayer';

export type AccordionItemProps = Sitecore.Override.ItemEx &
  Data.Accordion.Fields.AccordionItem & {
    isOpen?: {
      Open?: boolean;
      Refresh: boolean;
    };
  };

const AccordionItem = ({ fields, isOpen }: AccordionItemProps): React.JSX.Element => {
  const isNrc = useIsNRC();
  const { theme } = usePageSectionTheme();
  const [isActive, setIsActive] = useState(false);
  // needed to stop triggering "collapse" gtm on element creation
  const [isDefaultState, setIsDefaultState] = useState(true);
  const isPageEditing = useSitecoreContext().sitecoreContext.pageEditing;
  useEffect(() => {
    if (isOpen?.Open) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isActive && isDefaultState) {
      setIsDefaultState(false);
    }
    if (!isDefaultState) {
      gtmDatalayerPush({
        event: isActive ? 'e_expand' : 'e_collapse',
        contentName: fields?.heading.value,
      } as ContentDataLayerObject);
    }
  }, [isActive, fields.heading.value, isDefaultState]);

  const panelContainerClasses = isNrc
    ? classnames('bg-theme-secondary-bg', 'py-4')
    : classnames('bg-theme-secondary-bg', 'py-1.5', 'mb-2');

  const panelHeadingClasses = classnames('text-body', {
    'pl-2.5': !isNrc,
  });

  const panelArrowClasses = classnames(isNrc ? 'pr-4' : 'pr-2.5');
  let nrcArrowColorClass = classnames('text-blue-morning');

  if (theme === 'theme-blue') {
    nrcArrowColorClass = classnames('text-white');
  } else if (theme === 'theme-white-black' || theme === 'theme-whisper-black') {
    nrcArrowColorClass = classnames('text-black');
  }

  let arrowIcon: React.JSX.Element;

  if (isNrc) {
    if (isActive) {
      arrowIcon = <ArroweExpand className={nrcArrowColorClass} />;
    } else {
      arrowIcon = <ArrowCollapse className={nrcArrowColorClass} />;
    }
  } else if (isActive) {
    arrowIcon = <Collapse />;
  } else {
    arrowIcon = <Expand />;
  }

  return (
    <li className={classnames('accordion-item', { 'mb-2': !isNrc })}>
      <div className={panelContainerClasses}>
        {isPageEditing ? (
          <h2 className={classnames(panelHeadingClasses, 'flex')}>
            <span className={panelArrowClasses}>{arrowIcon}</span>
            <TextWithOptionalLineBreaks
              className={classnames('mb-2', 'text-body', 'pl-2.5', 'heading-sm')}
              tag=""
              field={fields?.heading}
              editable
            />
          </h2>
        ) : (
          <h2 className={classnames(panelHeadingClasses)}>
            <button
              type="button"
              className={classnames('font-semibold', 'flex', 'text-left')}
              onClick={() => setIsActive(!isActive)}
            >
              <span className={panelArrowClasses}>{arrowIcon}</span>
              <TextWithOptionalLineBreaks
                className={classnames('mb-2', 'text-body', 'pl-2.5', 'heading-sm')}
                tag=""
                field={fields?.heading}
                editable
              />
            </button>
          </h2>
        )}
      </div>
      <div className={classnames('pl-2.5')}>
        <div className={classnames('rte', { hidden: !isActive })}>
          <RichTextA11yWrapper field={fields.description} />
        </div>
      </div>
    </li>
  );
};

export default AccordionItem;
